import {
  GatewayAssujetti,
  GatewayCapacitePro,
  GatewayEntreprise,
} from "@conformite/gateway";
import {
  IconStatus,
  Link,
  isMulti,
  Form,
  Button,
  Card,
  FormTitle,
  displaySucessToast,
} from "@endya-conformite/ui-kit";
import ChevronLeft from "@endya-conformite/ui-kit/src/assets/icons/chevron-left.svg?react";
import Loader from "@endya-conformite/ui-kit/src/assets/loader.svg?react";
import { GatewayCapaciteProApi } from "@src/api/conformite/capacitePro.api";
import {
  ConformiteFormCapaciteProIASContent,
  useCapaciteProIAS,
} from "@src/assujettis/AssujettiPaneContent/pages/AssujettiParcoursConformite/Steps/FormsCapacitePro/ConformiteFormCapaciteProIAS/ConformiteFormCapaciteProIASContent";
import { MainContentPageTemplate } from "@src/components/MainPageTemplate/MainContentPageTemplate";
import { NoEntityForMillesime } from "@src/components/NoEntityForMillesime/NoEntityForMillesime";
import { useOnce } from "@src/hooks/useOnce";
import { useAssujetti } from "@src/store/store.assujetti";
import { useEntreprises } from "@src/store/store.entreprise";
import { useMillesime } from "@src/store/store.millesime";
import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import styles from "../assujettis/AssujettiForm.module.scss";

type ConformiteCapaciteProfessionnelleContentProps = {
  assujetti: GatewayAssujetti.AssujettiWithAvancement;
  entreprise: GatewayEntreprise.EntrepriseAControllerResponseType;
  fromAxe: string | null;
  capaciteProfessionnelle: GatewayCapacitePro.CapaciteProfessionnelleIAS | null;
};
function ConformiteCapaciteProfessionnelleContent({
  assujetti,
  fromAxe,
  entreprise,
  capaciteProfessionnelle,
}: ConformiteCapaciteProfessionnelleContentProps) {
  const isMonoActivite = !isMulti([
    ...entreprise.categoriesActivites,
    ...entreprise.categoriesActivitesAccessoires,
  ]);
  const {
    onSubmit,
    formConfig,
    typeJustificatifOptions,
    conditionCapaciteProfessionnelleValue,
  } = useCapaciteProIAS(
    { assujetti },
    capaciteProfessionnelle,
    isMonoActivite,
    () => {
      displaySucessToast("La conformité a bien été mise à jour");
    }
  );
  useOnce(() => {
    if (capaciteProfessionnelle !== null)
      formConfig.trigger().catch(console.error);
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  if (!entreprise || !assujetti) return null;
  return (
    <MainContentPageTemplate
      title={
        <FormTitle className={styles.header}>
          <h1 className={styles.title}>
            <b>Capacité Professionnelle IAS</b> - {assujetti.surname}{" "}
            {assujetti.name}
          </h1>
          <IconStatus
            isValid={assujetti.avancement.capaciteProfessionnelleIas === 100}
          />
        </FormTitle>
      }
      actionNavigation={
        <Link
          to={`/conformite/capacite-professionnelle/ias/${entreprise.siren}${
            fromAxe === null ? "" : "?fromAxe"
          }`}
        >
          <Button variant="text" icon={<ChevronLeft />}>
            Retour
          </Button>
        </Link>
      }
    >
      <Card>
        <Form formConfig={formConfig} onSubmit={onSubmit}>
          <ConformiteFormCapaciteProIASContent
            hideTitle
            data={{ assujetti }}
            conditionCapaciteProfessionnelleValue={
              conditionCapaciteProfessionnelleValue
            }
            typeJustificatifOptions={typeJustificatifOptions}
            formConfig={formConfig}
            isMonoActivite={isMonoActivite}
          />
        </Form>
        <div className={styles.action}>
          <Button
            disabled={isSubmitting}
            icon={isSubmitting ? <Loader /> : undefined}
            onClick={() => {
              setIsSubmitting(true);
              onSubmit(formConfig.getValues())
                .catch((e) => {
                  console.error(e);
                })
                .finally(() => setIsSubmitting(false));
            }}
          >
            Sauvegarder
          </Button>
        </div>
      </Card>
    </MainContentPageTemplate>
  );
}

export function ConformiteCapaciteProfessionnelleIas() {
  const { entreprisesBySiren } = useEntreprises();
  const { assujettis } = useAssujetti();
  const { selected: millesime } = useMillesime();
  const { siren, id } = useParams();
  const entreprise = entreprisesBySiren?.[siren || ""];
  const assujetti = assujettis?.[id || ""];
  const [query] = useSearchParams();
  const fromAxe = query.get("fromAxe");
  const [capaciteProfessionnelle, setCapaciteProfessionnelle] =
    useState<GatewayCapacitePro.CapaciteProfessionnelleIAS | null>();
  useEffect(() => {
    if (!assujetti) return;
    GatewayCapaciteProApi.getIAS(assujetti.id, assujetti.millesime)
      .then((data) => {
        setCapaciteProfessionnelle(data);
      })
      .catch((e) => {
        console.error(e);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assujetti]);
  const isDataLoaded =
    entreprisesBySiren !== undefined && assujettis !== undefined;
  if (isDataLoaded && (!entreprise || !assujetti)) {
    return (
      <MainContentPageTemplate title="Capacité Professionnelle IAS">
        <NoEntityForMillesime millesime={millesime} entityName="société" />
      </MainContentPageTemplate>
    );
  }
  if (
    !entreprise ||
    !assujetti ||
    !siren ||
    capaciteProfessionnelle === undefined
  )
    return null;
  return (
    <ConformiteCapaciteProfessionnelleContent
      entreprise={entreprise}
      assujetti={assujetti}
      fromAxe={fromAxe}
      capaciteProfessionnelle={capaciteProfessionnelle}
    />
  );
}
